//* Libraries imports
import { useQuery } from "@tanstack/react-query";

//* Schemas Import
import { templateEssay } from "../../../schemas/common/essay/essay";
import { createResponseFullSchema } from "../../../schemas/api-response";

//* Axios Import
import { privateApi } from "../../../services/apis/configs/apiClient";

//* Zod Import
import { z } from "zod";

const resultResponseSchema = createResponseFullSchema(templateEssay);

/**
 * API para redações
 */
const EssayAPI = {
  /**
   * Obtém o modelo de uma redação específica pelo seu ID
   * @param id ID do processo
   * @param essayId ID da redação específica
   * @returns Modelo completo da redação ou erro
   */
  getModel: async function (id: number, essayId: number) {
    if (!id || id <= 0) {
      return {
        data: null,
        success: false,
        message: "ID da redação é obrigatório e deve ser maior que zero.",
      };
    }

    try {
      const response = await privateApi.get(
        `/student_portal/private/processos/${id}/redacoes/${essayId}/modelo`
      );


      const validatedResponse = resultResponseSchema.parse(response);

      return {
        data: validatedResponse,
        success: true,
      };
    } catch (error) {
      console.error(`Erro ao buscar modelo da redação com ID ${id}:`, error);

      // Check if it's a Zod validation error
      if (error instanceof z.ZodError) {
        console.error("Schema validation error:", error.issues);
        return {
          data: null,
          success: false,
          message:
            "Estrutura de resposta da API inválida. Verifique o console para detalhes.",
        };
      }

      return {
        data: null,
        success: false,
        message:
          "Não foi possível carregar o modelo da redação. Tente novamente mais tarde.",
      };
    }
  },
};

/**
 * Hook para obter o modelo de uma redação específica pelo ID
 * @param id ID do processo a ser recuperado (deve ser um número válido > 0)
 * @param essayId ID da redação específica
 * @returns Query para modelo completo da redação
 */
export function useGetEssayById(id: number | null, essayId: number) {
  return useQuery({
    queryKey: ["essay-template", id, essayId],
    queryFn: async () => {
      if (!id || id <= 0) {
        throw new Error(
          "ID da redação é obrigatório e deve ser maior que zero."
        );
      }
      return await EssayAPI.getModel(id, essayId);
    },
    retry: (failureCount, error) => {
      if (error instanceof Error && error.message.includes("ID da redação")) {
        return false;
      }
      return failureCount < 2;
    },
    staleTime: 1000 * 60 * 5, 
    gcTime: 1000 * 60 * 10, 
    enabled: !!id && id > 0,
  });
}
